// Hearing a rhythm in a recording, to search with it (src/lib/similarity): where the hits are
// (onsets, by spectral flux), what plays each (the recording's own sounds, grouped, and named by
// the drums they sound nearest, played on a kit or beatboxed, and by where they fall in the bar),
// the tempo (from the onsets' autocorrelation, unless it's known, as in a DAW), the beat (followed
// as it drifts, as it does for anyone playing without a click), the downbeat (placed by the
// backbeat), and the hits on a 16th-or-triplet grid, written as a drum-set score. Plain math on
// samples: it runs in the browser on a recording or an uploaded file.
//
// Tuned on grooves beatboxed with real people's sounds (the Amateur Vocal Percussion dataset) and
// played on kits; scripts/audio-search/evaluate.js measures it.
import { writeVoice, TICKS } from "../grow/grid";

const RATE = 22050, SIZE = 1024, HOP = 256, FRAME = HOP / RATE;
// Ten bands, evenly spaced in pitch, from a kick's fundamental to a hi-hat's sizzle: where hits are,
// and how hard.
const EDGES = [40, 70, 120, 200, 350, 600, 1000, 1800, 3200, 5600, 11000];
// Sixteen, 60 Hz to 10 kHz: what each hit sounds like.
const TIMBRE = Array.from({ length: 17 }, (_, i) => 60 * Math.pow(10000 / 60, i / 16));
const PITCH = { kick: "F4", snare: "C5", hat: "E5" };
// What each drum sounds like in its first 60 ms (decibels per timbre band, less their mean; see
// shapeOf): played on a kit (the app's samples, and a synthesized kit), and beatboxed (the means of
// 28 people in the Amateur Vocal Percussion dataset, Delgado et al., CC BY 4.0). A kit's kick or
// snare often strikes with the hi-hat, so those pairs have sounds of their own.
const SOUNDS = {
  sampled: { kick: [22.8, 22.4, 21.2, 18.2, 7.9, -3.1, -5.2, -5.5, -3.7, -5.9, -8.6, -12.2, -12.2, -11.8, -12.2, -12.2], snare: [5.7, 3.9, 2, 5.2, 16.9, 6.9, 6.1, 6, 1.4, -1.2, -4.5, -4.9, -7.4, -8.5, -10.7, -16.7], hat: [-18.6, -11.2, -0.7, 2.7, 9.9, 7.7, 9.1, 4.3, -1.7, -0.5, 1.4, -1.5, 0.4, 1.9, 0.3, -3.6],
    "kick+hat": [15.9, 15.7, 14.5, 11.5, 4.2, 0.4, 1.6, -3.1, -7, -6.9, -5.9, -8.9, -7.3, -5.7, -7.4, -11.5], "snare+hat": [2.2, 0.5, -0.4, 3, 13.9, 5.5, 5.7, 3.7, -1.2, -2.7, -3, -4.9, -4.4, -3.4, -5.2, -9.2] },
  synthesized: { kick: [23, 27.4, 23.2, 10.5, -0.4, -7.6, -7.6, -7.6, -7.6, -7.6, -7.6, -7.6, -7.6, -7.6, -7.6, -7.6], snare: [2.4, 3.4, 5.5, 18.6, 10.4, -1.9, -3.8, -4, -4.3, -3.4, -3.7, -3.8, -3.7, -3.8, -3.7, -4.1], hat: [-5.5, -5.1, -5.5, -6, -6.3, -6.8, -7.4, -7.1, -7.3, -6.5, -2.2, 3.5, 7.7, 13.5, 18.5, 22.6],
    "kick+hat": [23.1, 27.4, 23.2, 10.4, -0.4, -7.6, -7.6, -7.6, -7.6, -7.6, -7.6, -7.6, -7.6, -7.6, -7.6, -7.5], "snare+hat": [2, 3.2, 5.2, 18.4, 10.2, -2.3, -3.8, -4.2, -4, -4, -4, -4.1, -3.5, -3.5, -3.2, -2.5] },
  voice: { kick: [-10.3, -1.9, 6.4, 10.5, 10.7, 13.8, 14.4, 7.5, 1.4, -1, -6.7, -7.8, -7, -9, -9.7, -11.4], snare: [-15.1, -10.7, -4.7, -0.4, 0.9, 4, 7.8, 6.8, 0.9, -1.8, 0.4, 0.1, 2.6, 3.7, 4.3, 1.1], hat: [-13.7, -11.2, -7.7, -3.9, -1.8, 2.4, 4, 0.6, -4.7, -6.4, -3.8, 4.2, 8.9, 11.1, 12.6, 9.4] },
  // And beatboxed, against the person's own sounds (each drum's, less the mean of their three):
  // one voice, one microphone and one room differ from the next more than their drums do.
  said: { kick: [2.7, 6.1, 8.4, 8.4, 7.5, 7, 5.6, 2.5, 2.2, 2.1, -3.3, -6.6, -8.5, -10.9, -12.1, -11.1], snare: [-2.1, -2.8, -2.7, -2.5, -2.4, -2.7, -0.9, 1.9, 1.7, 1.3, 3.8, 1.3, 1.1, 1.8, 1.9, 1.4], hat: [-0.7, -3.3, -5.7, -6, -5, -4.3, -4.7, -4.4, -3.9, -3.3, -0.5, 5.3, 7.4, 9.1, 10.2, 9.7] },
};
const LABELS = ["kick", "snare", "hat", "kick+hat", "snare+hat"];
// On a kit, where each drum's attack puts its energy across the ten bands: where learning a
// recording's kick, snare and hi-hat starts (see kitDrums).
const DRUMS = ["kick", "snare", "hat"];
const TEMPLATES = [[1, 1, 0.7, 0.35, 0.2, 0.15, 0.12, 0.15, 0.12, 0.06], [0.1, 0.2, 0.55, 0.9, 0.8, 0.7, 0.65, 0.75, 0.7, 0.5], [0.03, 0.04, 0.05, 0.08, 0.1, 0.15, 0.3, 0.6, 0.95, 1]]
  .map((row) => { const total = row.reduce((sum, value) => sum + value, 0); return row.map((value) => value / total); });
export const MAX_SECONDS = 30;
// The tuning, together (fitted to the grooves above by scripts/audio-search/evaluate.js; `voice`
// and `said` can stand in for SOUNDS.voice and SOUNDS.said there, so each beatboxer is heard with
// the others' sounds).
export const OPTIONS = {
  follow: 0.1, followDip: 6, followRatio: 0.8, followOver: 1.5, // a hit soon after another
  groupApart: 4.5, soundSpread: 6, nearby: 1.2, darkKick: 0.5, kickBright: 0.5, // grouping and naming sounds
  voiceLean: -2.5, voiceGap: 0.08, splitApart: 0.8, splitShare: 0.2, relative: 0.5, // beatboxing
  soundWeight: 3, kickOffBeat: 0, hatOnBackbeat: 0, halfTime: 0.75, // where drums fall
  errorWeight: 6, tripletWeight: 0.3, common: 0.2, commonSpread: 0.45, density: 0.45, perBeat: 2.8, densitySpread: 0.7, // which reading
  tightness: 20, smooth: 8, phaseBeats: 4, // following the beat
  voice: null, said: null,
};

// Mono samples at about 22 kHz (averaging neighbours on the way down), at most MAX_SECONDS long.
// Exactly to RATE from any rate (48 kHz, 44.1 kHz, 16 kHz...), so frame times and band edges hold:
// a windowed sinc (Blackman, 12 zero crossings a side, from a table), low-passed just under the
// lower Nyquist.
const KERNEL_STEPS = 256;
export function resample(samples, rate) {
  const limit = Math.min(samples.length, Math.ceil(MAX_SECONDS * rate));
  if (rate === RATE) return { samples: Float32Array.from(samples.subarray ? samples.subarray(0, limit) : samples.slice(0, limit)), rate };
  const ratio = rate / RATE, length = Math.floor(limit / ratio), stretch = Math.max(1, ratio), cutoff = 0.94 / stretch, reach = 12 * stretch;
  const kernel = new Float32Array(Math.ceil(reach * KERNEL_STEPS) + 2);
  for (let step = 0; step < kernel.length; step++) {
    const t = Math.min(reach, step / KERNEL_STEPS), x = Math.PI * cutoff * t;
    kernel[step] = (x === 0 ? 1 : Math.sin(x) / x) * (0.42 + 0.5 * Math.cos(Math.PI * t / reach) + 0.08 * Math.cos(2 * Math.PI * t / reach)) * cutoff;
  }
  const out = new Float32Array(length);
  for (let index = 0; index < length; index++) {
    const centre = index * ratio, first = Math.max(0, Math.ceil(centre - reach)), last = Math.min(limit - 1, Math.floor(centre + reach));
    let sum = 0;
    for (let i = first; i <= last; i++) sum += samples[i] * kernel[Math.round(Math.abs(i - centre) * KERNEL_STEPS)];
    out[index] = sum;
  }
  return { samples: out, rate: RATE };
}
// In-place radix-2 FFT of (re, im).
export function fft(re, im) {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) { [re[i], re[j]] = [re[j], re[i]]; [im[i], im[j]] = [im[j], im[i]]; }
  }
  for (let length = 2; length <= n; length <<= 1) {
    const angle = -2 * Math.PI / length, wr = Math.cos(angle), wi = Math.sin(angle);
    for (let start = 0; start < n; start += length) {
      let cr = 1, ci = 0;
      for (let k = 0; k < length / 2; k++) {
        const a = start + k, b = a + length / 2, tr = re[b] * cr - im[b] * ci, ti = re[b] * ci + im[b] * cr;
        re[b] = re[a] - tr; im[b] = im[a] - ti; re[a] += tr; im[a] += ti;
        const next = cr * wr - ci * wi; ci = cr * wi + ci * wr; cr = next;
      }
    }
  }
}
export const percentile = (values, q) => { const sorted = Float64Array.from(values).sort(); return sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))] : 0; };
const bandBins = (edges, rate) => edges.slice(0, -1).map((low, band) => { const from = Math.max(1, Math.round(low * SIZE / rate)); return [from, Math.max(from, Math.min(SIZE / 2, Math.round(edges[band + 1] * SIZE / rate) - 1))]; });

// Frame by frame: spectral flux in each band, rising energy on a log scale (`log`, for finding hits:
// every attack shows) and in plain magnitude (`linear`, for how hard: a kick's energy really is
// low, a hi-hat's high); the total energy in decibels (`energy`); and the power in each timbre band
// (`power`). Each is bands × frames.
function flux(samples, rate) {
  const frames = Math.max(0, Math.floor((samples.length - SIZE) / HOP) + 1), window = Float32Array.from({ length: SIZE }, (_, i) => 0.5 - 0.5 * Math.cos(2 * Math.PI * i / SIZE));
  const bins = bandBins(EDGES, rate), timbreBins = bandBins(TIMBRE, rate);
  const log = bins.map(() => new Float32Array(frames)), linear = bins.map(() => new Float32Array(frames)), energy = new Float32Array(frames), power = timbreBins.map(() => new Float32Array(frames));
  let previous = new Float32Array(SIZE / 2 + 1), previousPlain = new Float32Array(SIZE / 2 + 1);
  const re = new Float32Array(SIZE), im = new Float32Array(SIZE);
  for (let frame = 0; frame < frames; frame++) {
    for (let i = 0; i < SIZE; i++) { re[i] = samples[frame * HOP + i] * window[i]; im[i] = 0; }
    fft(re, im);
    const magnitude = new Float32Array(SIZE / 2 + 1), plain = new Float32Array(SIZE / 2 + 1);
    let total = 0;
    for (let k = 0; k <= SIZE / 2; k++) { plain[k] = Math.hypot(re[k], im[k]); magnitude[k] = Math.log1p(100 * plain[k]); total += plain[k] * plain[k]; }
    energy[frame] = 10 * Math.log10(total + 1e-9);
    bins.forEach(([from, to], band) => {
      let rise = 0, plainRise = 0;
      for (let k = from; k <= to; k++) { rise += Math.max(0, magnitude[k] - previous[k]); plainRise += Math.max(0, plain[k] - previousPlain[k]); }
      log[band][frame] = rise / (to - from + 1);
      linear[band][frame] = plainRise;
    });
    timbreBins.forEach(([from, to], band) => { let sum = 0; for (let k = from; k <= to; k++) sum += plain[k] * plain[k]; power[band][frame] = sum / (to - from + 1); });
    previous = magnitude; previousPlain = plain;
  }
  return { log, linear, energy, power };
}

// Onsets: peaks of the combined flux (each band against its own usual rise) above a running
// median, at least 45 ms apart (a hit's peaks are one hit: it starts at the first), each with how
// much each band rose and what it sounds like.
function onsetsOf({ log: bands, linear, energy, power }) {
  const frames = bands[0]?.length || 0, scale = bands.map((band) => percentile(band, 0.97) || 1);
  const combined = Float32Array.from({ length: frames }, (_, frame) => bands.reduce((sum, band, index) => sum + band[frame] / scale[index], 0));
  const top = percentile(combined, 0.99) || 1, found = [], reach = 12, gap = Math.ceil(0.045 / FRAME);
  const peakAt = (frame) => { let peak = -Infinity; for (let k = frame; k <= Math.min(frames - 1, frame + 3); k++) peak = Math.max(peak, energy[k]); return peak; };
  for (let frame = 1; frame < frames - 1; frame++) {
    const value = combined[frame];
    if (combined[frame - 1] > value || combined[frame + 1] > value) continue;
    const around = combined.slice(Math.max(0, frame - reach), Math.min(frames, frame + reach + 1));
    if (value < 1.5 * percentile(around, 0.5) + 0.12 * top) continue;
    const last = found[found.length - 1];
    if (last && frame - last.frame < gap) { last.strength = Math.max(last.strength, value); continue; }
    // Soon after a hit, another needs an attack of its own: the sound dips between them (a vowel
    // after a beatboxed "p" or "t" carries on from it), or it rises much harder.
    if (last && frame - last.frame < OPTIONS.follow / FRAME) {
      let low = Infinity;
      for (let k = last.frame; k <= frame; k++) low = Math.min(low, energy[k]);
      const dipped = Math.min(peakAt(last.frame), peakAt(frame)) - low >= OPTIONS.followDip && value >= OPTIONS.followRatio * last.strength;
      if (!dipped && value < OPTIONS.followOver * last.strength) { last.strength = Math.max(last.strength, value); continue; }
    }
    found.push({ frame, strength: value });
  }
  // A hit registers as the analysis window takes it in, about a quarter of the way in.
  return found.map(({ frame, strength }) => ({ time: frame * FRAME + SIZE / RATE / 4, strength,
    rise: Float32Array.from(linear, (band) => Math.max(band[frame], band[Math.min(frames - 1, frame + 1)])),
    // Its first 60 ms in decibels per timbre band: what's new, above what was sounding in the
    // windows just before (so a hi-hat over a ringing kick is a hi-hat).
    timbre: Float32Array.from(power, (band) => {
      let before = Infinity;
      for (let k = Math.max(0, frame - 7); k <= frame - 4; k++) before = Math.min(before, band[k]);
      if (!Number.isFinite(before)) before = 0;
      let sum = 0;
      for (let k = frame; k <= Math.min(frames - 1, frame + 4); k++) sum += Math.max(0, band[k] - before);
      return 10 * Math.log10(sum / 5 + 1e-10);
    }) }));
}

// The value splitting a list into two groups as cleanly as possible (Otsu's method), or null when
// the list doesn't divide clearly (the groups' spread is under 3/5 of the whole).
function otsu(values) {
  const sorted = [...values].sort((a, b) => a - b), n = sorted.length;
  if (n < 4) return null;
  const mean = sorted.reduce((sum, value) => sum + value, 0) / n, spread = sorted.reduce((sum, value) => sum + (value - mean) ** 2, 0) / n;
  if (spread < 1e-9) return null;
  let best = -1, split = null, sum = 0;
  for (let index = 0; index < n - 1; index++) {
    sum += sorted[index];
    const low = (index + 1) / n, high = 1 - low, lowMean = sum / (index + 1), highMean = (mean * n - sum) / (n - index - 1);
    const between = low * high * (lowMean - highMean) ** 2;
    if (between > best) { best = between; split = (sorted[index] + sorted[index + 1]) / 2; }
  }
  return best / spread >= 0.6 ? split : null;
}
// What plays each hit on a kit, where drums strike together: every hit's band rises explained as a
// mix of three spectra (non-negative matrix factorization), learned from this recording's hits
// starting from a kick's, a snare's and a hi-hat's, so any kit, room or microphone is heard on its
// own terms. A drum plays in a hit when it explains a fair share of it and is near its own usual
// strength there.
function kitDrums(onsets) {
  const n = onsets.length;
  // One drum alone (a snare part, a pad, a table): every hit has the same spectrum, which three
  // drums would only split arbitrarily. A kit's hits swing between low (kicks) and high
  // (cymbals); one drum's barely move (under 0.035 in the tests, a kit 0.115 and up). It's
  // whichever drum its spectrum is nearest.
  const shapes = onsets.map((onset) => { const total = onset.rise.reduce((sum, value) => sum + value, 0) || 1; return Array.from(onset.rise, (value) => value / total); });
  const mean = Array.from({ length: EDGES.length - 1 }, (_, band) => shapes.reduce((sum, shape) => sum + shape[band], 0) / n);
  const cosine = (a, b) => { let dot = 0, left = 0, right = 0; for (let i = 0; i < a.length; i++) { dot += a[i] * b[i]; left += a[i] * a[i]; right += b[i] * b[i]; } return left && right ? dot / Math.sqrt(left * right) : 0; };
  const spread = (values) => { const average = values.reduce((sum, value) => sum + value, 0) / values.length; return Math.sqrt(values.reduce((sum, value) => sum + (value - average) ** 2, 0) / values.length); };
  if (spread(shapes.map((shape) => shape[0] + shape[1] + shape[2])) + spread(shapes.map((shape) => shape[7] + shape[8] + shape[9])) < 0.06) {
    const nearest = DRUMS[TEMPLATES.map((template) => cosine(template, mean)).reduce((best, value, index, all) => value > all[best] ? index : best, 0)];
    for (const onset of onsets) onset.kinds = [nearest];
    return;
  }
  const W = TEMPLATES.map((row) => Float64Array.from(row)), H = DRUMS.map(() => new Float64Array(n).fill(1)), epsilon = 1e-9, bands = EDGES.length - 1;
  const V = onsets.map((onset) => onset.rise);
  const model = (j, band) => { let sum = 0; for (let c = 0; c < 3; c++) sum += W[c][band] * H[c][j]; return sum; };
  for (let round = 0; round < 120; round++) {
    // Activations: H ← H · (WᵀV) / (WᵀWH).
    for (let j = 0; j < n; j++) for (let c = 0; c < 3; c++) {
      let up = 0, down = 0;
      for (let band = 0; band < bands; band++) { up += W[c][band] * V[j][band]; down += W[c][band] * model(j, band); }
      H[c][j] *= up / (down + epsilon);
    }
    // Spectra: W ← W · (VHᵀ) / (WHHᵀ), held near where they started, and normalized.
    for (let c = 0; c < 3; c++) {
      for (let band = 0; band < bands; band++) {
        let up = 0, down = 0;
        for (let j = 0; j < n; j++) { up += V[j][band] * H[c][j]; down += model(j, band) * H[c][j]; }
        W[c][band] = 0.8 * W[c][band] * up / (down + epsilon) + 0.2 * TEMPLATES[c][band];
      }
      const total = W[c].reduce((sum, value) => sum + value, 0) || 1;
      for (let band = 0; band < bands; band++) W[c][band] /= total;
      for (let j = 0; j < n; j++) H[c][j] *= total;
    }
  }
  // A drum plays in the hits where its activation stands with its strong ones: its activations
  // split in two (Otsu's threshold, on a log scale) when they fall clearly into hits and leakage,
  // else a third of its strongest; and it explains a fair part of the hit. A drum leaks a little
  // into hits it isn't in, the same amount each time: where its shares in hits another drum leads
  // split into leaks and real hits, it needs the real share.
  const thresholds = H.map((row) => otsu(Array.from(row, (value) => Math.log(value + 1e-6))) ?? Math.log(0.33 * Math.max(...row) + 1e-6));
  const shares = onsets.map((_, j) => { const total = DRUMS.reduce((sum, __, c) => sum + H[c][j], 0) || 1; return DRUMS.map((__, c) => H[c][j] / total); });
  const least = DRUMS.map((_, c) => Math.max(0.15, Math.min(0.3, otsu(shares.filter((share) => share[c] < Math.max(...share)).map((share) => share[c])) ?? 0)));
  onsets.forEach((onset, j) => {
    const share = shares[j];
    onset.kinds = DRUMS.filter((_, c) => Math.log(H[c][j] + 1e-6) >= thresholds[c] && share[c] >= least[c]);
    if (!onset.kinds.length) onset.kinds = [DRUMS[share.indexOf(Math.max(...share))]];
  });
}

// A sound's shape: its decibels per band, at most 35 below its loudest, less their mean.
export function shapeOf(timbre) {
  const floor = Math.max(...timbre) - 35, values = Array.from(timbre, (value) => Math.max(floor, value)), mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  return values.map((value) => value - mean);
}
// How far apart two shapes are: root mean square, in decibels.
const apart = (a, b) => { let sum = 0; for (let i = 0; i < a.length; i++) sum += (a[i] - b[i]) ** 2; return Math.sqrt(sum / a.length); };
// A deterministic generator, so a recording is always heard the same way.
function generator(seed) { let state = seed >>> 0; return () => { state = (Math.imul(state, 1664525) + 1013904223) >>> 0; return state / 4294967296; }; }
// k-means (k-means++ starts, the best of a few runs): each point's group and the groups' centres.
function kmeans(points, k, random, runs = 6) {
  const n = points.length, d = points[0].length;
  let best = null;
  for (let run = 0; run < runs; run++) {
    const centres = [points[Math.floor(random() * n)].slice()];
    while (centres.length < k) {
      const weights = points.map((point) => Math.min(...centres.map((centre) => apart(point, centre) ** 2))), total = weights.reduce((sum, value) => sum + value, 0);
      let target = random() * total, index = 0;
      while (index < n - 1 && target > weights[index]) target -= weights[index++];
      centres.push(points[index].slice());
    }
    let assign = new Array(n).fill(-1);
    for (let round = 0; round < 40; round++) {
      const next = points.map((point) => { let nearest = 0; for (let c = 1; c < k; c++) if (apart(point, centres[c]) < apart(point, centres[nearest])) nearest = c; return nearest; });
      if (next.every((value, i) => value === assign[i])) break;
      assign = next;
      for (let c = 0; c < k; c++) {
        const members = points.filter((_, i) => assign[i] === c);
        if (members.length) for (let j = 0; j < d; j++) centres[c][j] = members.reduce((sum, point) => sum + point[j], 0) / members.length;
      }
    }
    const spread = points.reduce((sum, point, i) => sum + apart(point, centres[assign[i]]) ** 2, 0);
    if (!best || spread < best.spread) best = { assign, centres, spread };
  }
  return best;
}
// The recording's sounds: the most groups (up to five) whose sounds stay clearly apart, each of a
// few hits. (One person's snare and hi-hat can sound much alike; where they fall tells them apart.)
function groupsOf(onsets) {
  const points = onsets.map((onset) => onset.shape), random = generator(points.length * 7919 + 1);
  let chosen = { assign: points.map(() => 0), centres: [points[0].map((_, j) => points.reduce((sum, point) => sum + point[j], 0) / points.length)] };
  for (let k = 2; k <= Math.min(5, Math.floor(points.length / 3)); k++) {
    const run = kmeans(points, k, random), sizes = run.centres.map((_, c) => run.assign.filter((value) => value === c).length);
    let closest = Infinity;
    for (let a = 0; a < k; a++) for (let b = a + 1; b < k; b++) closest = Math.min(closest, apart(run.centres[a], run.centres[b]));
    if (closest >= OPTIONS.groupApart && Math.min(...sizes) >= 2) chosen = run;
  }
  return chosen;
}
// What a group of hits could be: each drum (or kit pair) it sounds near enough (or, with `all`,
// every one), as the log-likelihood of its sound against the nearest of that name, best first.
function namesFor(centre, voice, beatboxed, all = false, own = null) {
  // (A beatboxer makes one sound at a time: no pairs.)
  const said = OPTIONS.said || SOUNDS.said;
  const scored = LABELS.filter((label) => !beatboxed || !label.includes("+")).map((label) => {
    let distance = Math.min(...[SOUNDS.sampled[label], SOUNDS.synthesized[label], voice[label]].filter(Boolean).map((sound) => apart(centre, sound)));
    if (own) distance = (1 - OPTIONS.relative) * distance + OPTIONS.relative * apart(centre.map((value, band) => value - own[band]), said[label]);
    return { kinds: label.split("+"), score: -(distance ** 2) / (2 * OPTIONS.soundSpread ** 2) };
  }).sort((a, b) => b.score - a.score);
  if (all) return scored;
  // Beatboxed, a snare and a hi-hat can sound much alike (nearly the same "t"), so either may be the
  // other wherever it falls; anything else, only when it sounds near enough.
  const swap = beatboxed ? { snare: "hat", hat: "snare" }[scored[0].kinds.join("+")] : null;
  const options = scored.filter((option, index) => index === 0 || option.score >= scored[0].score - OPTIONS.nearby || option.kinds.join("+") === swap).slice(0, 3);
  // And a group can hold both, alike enough to have grouped together: the snare where the backbeat
  // is, the hi-hat elsewhere (when those hits do sound apart; see place).
  if (swap) options.push({ kinds: null, split: true, score: Math.max(...scored.filter((option) => ["snare", "hat"].includes(option.kinds.join("+"))).map((option) => option.score)) });
  return options;
}
// The recording's sounds, each hit with its group, and each group with what it could be (one sound
// alone, a snare part or a table tapped or "ta ta ta", is a snare); played on a kit, each hit
// with its drums. `onsets.voice`: how much nearer the sounds are to a beatboxer's than to a kit's.
function soundsOf(onsets) {
  if (!onsets.length) return [];
  for (const onset of onsets) onset.shape = shapeOf(onset.timbre);
  const groups = groupsOf(onsets), voice = OPTIONS.voice || SOUNDS.voice;
  const nearest = (centre, set) => Math.min(...Object.values(set).map((sound) => apart(centre, sound)));
  onsets.voice = groups.assign.reduce((sum, group) => sum + Math.min(nearest(groups.centres[group], SOUNDS.sampled), nearest(groups.centres[group], SOUNDS.synthesized)) - nearest(groups.centres[group], voice), 0) / onsets.length;
  const beatboxed = onsets.voice > OPTIONS.voiceLean;
  // Played on a kit, each hit is its own: its drums from its sound (see kitDrums).
  if (!beatboxed) {
    kitDrums(onsets);
    onsets.forEach((onset, j) => { onset.group = j; });
    return onsets.map((onset) => ({ options: [{ kinds: onset.kinds, score: 0 }], size: 1, brightness: 0 }));
  }
  // (The person's own sound: the mean of their drums', each drum the groups it first sounds like.)
  const kindOf = groups.centres.map((centre) => namesFor(centre, voice, beatboxed)[0].kinds.join("+")), kinds = [...new Set(kindOf)];
  const own = groups.centres[0].map((_, band) => kinds.reduce((sum, kind) => { let total = 0, count = 0; groups.centres.forEach((centre, group) => { if (kindOf[group] === kind) { const size = groups.assign.filter((value) => value === group).length; total += size * centre[band]; count += size; } }); return sum + total / count; }, 0) / kinds.length);
  const names = groups.centres.length === 1 ? [[{ kinds: ["snare"], score: 0 }]] : groups.centres.map((centre) => namesFor(centre, voice, beatboxed, false, own));
  // Anyone's kick is their darkest sound (27 of the 28 beatboxers; a kit's, always): the darkest
  // group may always be the kick, and beatboxed, it is, unless where it falls says otherwise.
  const brightness = groups.centres.map((centre) => { const power = centre.map((value) => Math.pow(10, value / 10)), total = power.reduce((sum, value) => sum + value, 0); return power.reduce((sum, value, band) => sum + value * band, 0) / total; });
  const darkest = brightness.indexOf(Math.min(...brightness));
  if (names.length > 1 && !names[darkest].some((option) => option.kinds?.includes("kick"))) {
    const kick = namesFor(groups.centres[darkest], voice, beatboxed, true, own).find((option) => option.kinds.join("+") === "kick");
    if (kick) names[darkest].push(kick);
  }
  if (names.length > 1 && beatboxed) names.forEach((options, group) => {
    for (const option of options) if (option.kinds?.includes("kick")) option.score += group === darkest ? OPTIONS.darkKick : -OPTIONS.darkKick;
    options.sort((a, b) => b.score - a.score);
  });
  onsets.forEach((onset, j) => { onset.group = groups.assign[j]; onset.kinds = names[onset.group].find((option) => option.kinds).kinds; });
  return names.map((options, group) => ({ options, size: groups.assign.filter((value) => value === group).length, brightness: brightness[group] }));
}

// The beat's period: the strongest autocorrelation of the onsets, with its half, double and
// bar (four beats, which a groove repeats and a wrong tempo doesn't), and a preference for
// moderate tempos; unless the tempo is given.
export function periodOf(onsets, duration) {
  const resolution = 0.01, length = Math.ceil(duration / resolution) + 1, strength = new Float32Array(length);
  for (const onset of onsets) { const at = Math.round(onset.time / resolution); for (let k = -2; k <= 2; k++) if (at + k >= 0 && at + k < length) strength[at + k] += onset.strength * Math.exp(-k * k / 2); }
  const correlation = (lag) => { let sum = 0; for (let i = 0; i + lag < length; i++) sum += strength[i] * strength[i + lag]; return sum / Math.max(1, length - lag); };
  const at = (value) => { const low = Math.floor(value), part = value - low; return (1 - part) * correlation(low) + part * correlation(low + 1); };
  let best = null;
  for (let bpm = 55; bpm <= 210; bpm += 0.5) {
    const lag = 60 / bpm / resolution, bar = 4 * lag < length * 0.75 ? 0.5 * at(4 * lag) : 0;
    const value = (at(lag) + 0.5 * at(2 * lag) + bar + 0.25 * at(lag / 2)) * Math.exp(-((Math.log2(bpm / 105)) ** 2) / (2 * 0.7 ** 2));
    if (!best || value > best.value) best = { bpm, value };
  }
  return 60 / best.bpm;
}
// How much a hit counts toward the beat: kicks and snares mark it, hi-hats subdivide it.
const PHASE_WEIGHT = { kick: 2, snare: 1.5, hat: 0.5 };
const weightOf = (onset) => Math.max(...(onset.kinds || ["snare"]).map((kind) => PHASE_WEIGHT[kind] ?? 1));
// The phase the hits fall on, for a period: the kick and snare count most (hi-hats on every 8th
// fit the off-beats as well as the beats), the first bars most (a drifting tempo smears the rest),
// and the first hit tips a close call (people start on the beat).
function phaseOf(onsets, period) {
  let phase = 0, best = -Infinity;
  const first = onsets[0]?.time || 0;
  for (let step = 0; step < 48; step++) {
    const candidate = step * period / 48, offset = (value) => { const distance = (((value - candidate) / period) % 1 + 1) % 1; return Math.min(distance, 1 - distance); };
    let value = 0;
    for (const onset of onsets) value += onset.strength * weightOf(onset) * Math.exp(-((offset(onset.time) / 0.06) ** 2)) * Math.exp(-(onset.time - first) / (OPTIONS.phaseBeats * period));
    if (offset(first) < 0.06) value *= 1.1;
    if (value > best) { best = value; phase = candidate; }
  }
  return phase;
}
export const snapBeat = (position) => { const whole = Math.floor(position), part = position - whole, sixteenth = Math.round(part * 4) / 4, triplet = Math.round(part * 3) / 3; return whole + (Math.abs(part - sixteenth) <= Math.abs(part - triplet) ? sixteenth : triplet); };

// The beat: steady (the time of beat x is phase + period·x, as from a DAW) or followed (`times`,
// each beat's, from track): where a time falls in it, when a beat falls, how long a beat lasts.
export function beatAt(beat, time) {
  if (!beat.times) return (time - beat.phase) / beat.period;
  const times = beat.times, last = times.length - 1;
  if (time <= times[0]) return (time - times[0]) / (times[1] - times[0]);
  if (time >= times[last]) return last + (time - times[last]) / (times[last] - times[last - 1]);
  let low = 0, high = last;
  while (high - low > 1) { const middle = (low + high) >> 1; if (times[middle] <= time) low = middle; else high = middle; }
  return low + (time - times[low]) / (times[low + 1] - times[low]);
}
export function timeAt(beat, x) {
  if (!beat.times) return beat.phase + beat.period * x;
  const times = beat.times, i = Math.max(0, Math.min(times.length - 2, Math.floor(x)));
  return times[i] + (x - i) * (times[i + 1] - times[i]);
}
function periodAt(beat, x) {
  if (!beat.times) return beat.period;
  const times = beat.times, i = Math.max(0, Math.min(times.length - 2, Math.floor(x)));
  return times[i + 1] - times[i];
}
// The beats through a recording at about `period`, as a listener follows them: by dynamic
// programming (Ellis, "Beat tracking by dynamic programming", 2007), each beat on as strong a hit
// as it can be (kicks and snares count most), each beat's length near the last's, so a drifting
// tempo is followed and one stray hit doesn't throw it; then steadied, each beat where a line
// through its neighbours puts it (it drifts as the playing does, without wobbling with every hit).
// Beats run a little past the hits both ways. { times }, or null.
export function track(onsets, period, duration) {
  const step = 0.01, frames = Math.ceil(duration / step) + 2, envelope = new Float32Array(frames);
  for (const onset of onsets) {
    const at = Math.round(onset.time / step), weight = onset.strength * weightOf(onset);
    for (let k = -2; k <= 2; k++) if (at + k >= 0 && at + k < frames) envelope[at + k] += weight * Math.exp(-k * k / 2);
  }
  const top = Math.max(...envelope) || 1;
  for (let i = 0; i < frames; i++) envelope[i] /= top;
  const lag = period / step, score = new Float32Array(frames), from = new Int32Array(frames).fill(-1);
  for (let t = 0; t < frames; t++) {
    let best = -Infinity, arg = -1;
    for (let p = Math.max(0, Math.round(t - 2 * lag)); p <= t - Math.round(lag / 2); p++) {
      const value = score[p] - OPTIONS.tightness * Math.log((t - p) / lag) ** 2;
      if (value > best) { best = value; arg = p; }
    }
    score[t] = envelope[t] + (arg >= 0 ? best : 0); from[t] = arg;
  }
  // The last beat: the best near the end; then back through each beat's predecessor.
  let end = frames - 1;
  for (let t = Math.max(0, Math.round(frames - 1 - lag)); t < frames; t++) if (score[t] > score[end]) end = t;
  const times = [];
  for (let t = end; t >= 0; t = from[t]) times.unshift(t * step);
  if (times.length < 2) return null;
  if (times.length >= 4) {
    const raw = times.slice();
    for (let i = 0; i < raw.length; i++) {
      const low = Math.max(0, i - OPTIONS.smooth), high = Math.min(raw.length - 1, i + OPTIONS.smooth), n = high - low + 1;
      let sx = 0, sy = 0, sxx = 0, sxy = 0;
      for (let j = low; j <= high; j++) { sx += j; sy += raw[j]; sxx += j * j; sxy += j * raw[j]; }
      const slope = (n * sxy - sx * sy) / (n * sxx - sx * sx || 1);
      times[i] = (sy - slope * sx) / n + slope * i;
    }
  }
  const first = onsets[0].time, last = onsets[onsets.length - 1].time;
  while (times[0] > first - 1e-6) times.unshift(times[0] - (times[1] - times[0]));
  while (times[times.length - 1] < last + 1e-6) times.push(times[times.length - 1] + (times[times.length - 1] - times[times.length - 2]));
  return { times };
}
// A followed beat moved on by part of a beat (a syncopated groove fits the off-beat too).
export const shifted = (beat, part) => ({ times: beat.times.map((time, i, times) => time + part * (i + 1 < times.length ? times[i + 1] - time : time - times[i - 1])) });
// A steady beat refined to the hits: its phase, through onset times against their grid places.
function refine(onsets, beat) {
  const points = onsets.map((onset) => [snapBeat(beatAt(beat, onset.time)), onset.time]), n = points.length;
  if (n < 4) return beat;
  const mx = points.reduce((s, [x]) => s + x, 0) / n, my = points.reduce((s, [, y]) => s + y, 0) / n;
  return { period: beat.period, phase: my - beat.period * mx };
}

// The hits on the grid for a beat, in bars: each beat on whichever grid fits its hits better
// (16ths, or 8th-note triplets); then what each group of sounds is and where the bar starts,
// together: the naming and downbeat (given, or chosen) that put the kick on 1 and 3 and the snare
// on 2 and 4 (or 3, half-time) most, from among the names each group's sound allows, weighed with
// how near its sound is to them. Of close calls, the bar starting on the first hit (people start at
// the top). `fit` is how well the bars fit that backbeat; `sound`, how far the naming strays from
// the sounds (per hit); `error`, how far the hits were from the grid (seconds); `triplets`, the
// share of hits only a triplet grid places. Each hit gets its `slot` (beats) and `kinds`.
function place(onsets, beat, perBar, loud, knownDownbeat, sounds) {
  const hits = onsets.map((onset) => ({ beat: beatAt(beat, onset.time), group: onset.group, strength: onset.force / loud }));
  const byBeat = new Map();
  for (const hit of hits) { const index = Math.floor(hit.beat + 1 / 24); if (!byBeat.has(index)) byBeat.set(index, []); byBeat.get(index).push(hit); }
  let error = 0;
  for (const [index, list] of byBeat) {
    const miss = (steps) => list.reduce((sum, hit) => { const part = hit.beat - index; return sum + Math.abs(part - Math.round(part * steps) / steps); }, 0);
    const steps = miss(3) * 1.15 < miss(4) ? 3 : 4;
    // (In seconds: in beats, the same playing would sit twice as close to a grid read at half the tempo.)
    const seconds = periodAt(beat, index);
    for (const hit of list) { const part = hit.beat - index; hit.slot = index + Math.round(part * steps) / steps; error += Math.abs(part - (hit.slot - index)) * seconds; }
  }
  const first = Math.floor(Math.min(...hits.map((hit) => hit.slot)) + 1e-6);
  const triplets = hits.filter((hit) => { const part = ((hit.slot % 1) + 1) % 1; return Math.abs(part - 1 / 3) < 1e-6 || Math.abs(part - 2 / 3) < 1e-6; }).length / hits.length;
  const startsOnBeat = Math.abs(hits[0].slot - Math.round(hits[0].slot)) < 1e-6;
  // Where each hit falls in a bar starting `offset` beats after the first.
  for (const hit of hits) hit.from = hit.slot - first;
  const inBarOf = (hit, offset) => ((hit.from - offset) % perBar + perBar) % perBar;
  const backbeat = (hit, offset) => { const inBar = inBarOf(hit, offset); return Math.abs(inBar - Math.round(inBar)) < 1e-6 && Math.round(inBar) % 2 === 1; };
  // A group split by where its hits fall holds two sounds only if the hits on the backbeat sound
  // apart from the rest, and each is a real part of the group (a few stray hits aren't a sound).
  const splits = new Map();
  const splitHolds = (group, offset) => {
    const key = `${group}:${offset}`;
    if (!splits.has(key)) {
      const on = [], off = [];
      hits.forEach((hit, j) => { if (hit.group === group) (backbeat(hit, offset) ? on : off).push(onsets[j].shape); });
      let holds = false;
      if (Math.min(on.length, off.length) >= Math.max(2, OPTIONS.splitShare * (on.length + off.length))) {
        const mean = (list) => list[0].map((_, band) => list.reduce((sum, shape) => sum + shape[band], 0) / list.length), a = mean(on), b = mean(off);
        const spread = Math.sqrt((on.reduce((sum, shape) => sum + apart(shape, a) ** 2, 0) + off.reduce((sum, shape) => sum + apart(shape, b) ** 2, 0)) / (on.length + off.length)) || 1;
        holds = apart(a, b) / spread >= OPTIONS.splitApart;
      }
      splits.set(key, holds);
    }
    return splits.get(key);
  };
  const drumsOf = (kinds, hit, offset) => kinds[hit.group] === "split" ? (backbeat(hit, offset) ? ["snare"] : ["hat"]) : kinds[hit.group];
  const fitAt = (kinds, offset) => {
    kinds.forEach((drums, group) => { if (drums === "split" && !splitHolds(group, offset)) kinds = null; });
    if (!kinds) return -Infinity;
    let value = 0;
    for (const hit of hits) {
      const drums = drumsOf(kinds, hit, offset), inBar = inBarOf(hit, offset), kick = drums.includes("kick"), snare = drums.includes("snare");
      if (Math.abs(inBar - Math.round(inBar)) > 1e-6) { if (kick || snare) value -= 0.15 * hit.strength; continue; }
      const strong = Math.round(inBar) % 2 === 0;
      if (kick) value += strong ? hit.strength : -OPTIONS.kickOffBeat * hit.strength;
      // (A snare on 3 of a bar of four is a half-time backbeat.)
      if (snare) value += !strong ? hit.strength : perBar === 4 && Math.round(inBar) === 2 ? OPTIONS.halfTime * hit.strength : -0.5 * hit.strength;
      // A hi-hat alone on 2 or 4 is an 8th note read as a beat: the tempo is twice what it is.
      else if (!kick && !strong) value -= OPTIONS.hatOnBackbeat * hit.strength;
      if (Math.round(inBar) === 0) value += 0.25 * hit.strength;
    }
    return value;
  };
  const total = hits.reduce((sum, hit) => sum + hit.strength, 0) || 1, n = hits.length;
  // Every naming the groups' sounds allow, with how far each strays from them; no kick brighter
  // than a snare or hi-hat.
  let namings = [{ kinds: [], sound: 0 }];
  for (const { options, size } of sounds) namings = namings.flatMap((naming) => options.map((option) => ({ kinds: [...naming.kinds, option.split ? "split" : option.kinds], sound: naming.sound + size * (option.score - options[0].score) })));
  namings = namings.filter((naming) => {
    let kickTop = -Infinity, otherLow = Infinity;
    naming.kinds.forEach((kinds, group) => { if (kinds !== "split" && kinds.includes("kick")) kickTop = Math.max(kickTop, sounds[group].brightness); else otherLow = Math.min(otherLow, sounds[group].brightness); });
    return sounds.length === 1 || kickTop <= otherLow + OPTIONS.kickBright;
  });
  const oneSound = sounds.length === 1;
  let best = null;
  for (const naming of namings) {
    let downbeat = first, fit;
    if (knownDownbeat != null) {
      // Bar lines from the given downbeat; the bar the first hit falls in.
      const barLine = Math.round(beatAt(beat, knownDownbeat));
      downbeat = barLine + Math.floor((first - barLine) / perBar) * perBar;
      fit = fitAt(naming.kinds, ((downbeat - first) % perBar + perBar) % perBar);
    } else {
      const fits = Array.from({ length: perBar }, (_, offset) => oneSound ? (offset ? -1 : 1) : fitAt(naming.kinds, offset)), top = Math.max(...fits);
      const near = fits.map((value, offset) => [value, offset]).filter(([value]) => value >= top - 0.1 * Math.abs(top));
      downbeat = first + (near.some(([, offset]) => offset === 0) ? 0 : near.sort((a, b) => b[0] - a[0])[0][1]);
      while (downbeat > first + 1e-6) downbeat -= perBar;
      fit = top;
    }
    const value = fit / total + OPTIONS.soundWeight * naming.sound / n;
    if (!best || value > best.value) best = { value, downbeat, fit, kinds: naming.kinds, sound: naming.sound / n };
  }
  if (!best || !Number.isFinite(best.value)) return null;
  const offset = ((best.downbeat - first) % perBar + perBar) % perBar;
  for (const hit of hits) hit.kinds = drumsOf(best.kinds, hit, offset);
  return { hits, downbeat: best.downbeat, beat, error, fit: best.fit / total, sound: best.sound, triplets, startsOnBeat };
}

// The hits in a recording, grouped by sound. A beatboxer makes one sound at a time and never two
// within 80 ms: anything that soon after a hit is its vowel or breath.
function heardHits(samples, rate) {
  let onsets = onsetsOf(flux(samples, rate)), sounds = soundsOf(onsets);
  if (onsets.voice > OPTIONS.voiceLean) {
    const kept = [];
    for (const onset of onsets) { const last = kept[kept.length - 1]; if (last && onset.time - last.time < OPTIONS.voiceGap) last.strength = Math.max(last.strength, onset.strength); else kept.push(onset); }
    if (kept.length < onsets.length) { onsets = kept; sounds = soundsOf(onsets); }
  }
  return { onsets, sounds };
}
// The hits in a recording's samples, each with what it sounds like: { time (seconds), strength,
// rise (per band), timbre, group, kinds (by its sound alone) }.
export function hear(input, sampleRate) {
  const { samples, rate } = resample(input, sampleRate);
  return heardHits(samples, rate).onsets;
}

// A drum-set score from a recording's samples (mono, any sample rate): { score, tempo, timeSig,
// hits, confidence (0–1: how well the hits sit on the grid), beat, onsets ({ time, kinds }),
// beatboxed (whether it sounds said rather than played) }, or null without a rhythm. `tempo` (BPM) is used when known; `downbeat` (seconds into the samples)
// places bar 1 when known, as a DAW does (and then the tempo is steady).
export function transcribe(input, sampleRate, { timeSig = { num: 4, type: 4 }, bars: most = 4, tempo = null, downbeat: knownDownbeat = null } = {}) {
  const { samples, rate } = resample(input, sampleRate), { onsets, sounds } = heardHits(samples, rate);
  if (onsets.length < 3) return null;
  const duration = samples.length / rate, given = Number.isFinite(tempo) && tempo > 20 ? 60 / tempo / (timeSig.type === 8 ? 2 : 1) : null;
  // How hard each hit is: its attack's plain magnitude (the log scale finds hits, but squashes accents).
  for (const onset of onsets) onset.force = onset.rise.reduce((sum, value) => sum + value, 0);
  const perBar = timeSig.num * 4 / timeSig.type, loud = percentile(onsets.map((onset) => onset.force), 0.5) || 1;
  let placed;
  if (knownDownbeat != null) {
    const period = given || periodOf(onsets, duration);
    placed = place(onsets, refine(onsets, refine(onsets, { period, phase: ((knownDownbeat % period) + period) % period })), perBar, loud, knownDownbeat, sounds);
  } else {
    // For the likeliest tempo (or the one given) and its double, half, three-halves, two-thirds,
    // three-quarters and four-thirds (a groove's dotted figures can pass for a beat: a dotted 8th,
    // or three of them across two beats), the beat followed through the recording and moved on by
    // each quarter of a beat (a syncopated groove fits the off-beat too), each placed in bars.
    const likeliest = given || periodOf(onsets, duration);
    const periods = given ? [given] : [1, 0.5, 2, 2 / 3, 1.5, 4 / 3, 3 / 4].map((factor) => likeliest * factor).filter((value) => value >= 60 / 210 && value <= 60 / 55);
    periods.forEach((period, index) => {
      const followed = track(onsets, period, duration), strongest = followed ? 0 : phaseOf(onsets, period);
      for (let shift = 0; shift < 4; shift++) {
        const beat = followed ? shifted(followed, shift / 4) : refine(onsets, { period, phase: strongest + shift * period / 4 });
        const candidate = place(onsets, beat, perBar, loud, null, sounds);
        if (!candidate) continue;
        // The one whose hits sit on the grid best, with the clearest backbeat, wins. Off the grid
        // costs, and so do triplets a straight reading could do without (straight 8ths at
        // four-thirds the tempo are triplets); tempos people play at count, and a few hits to a
        // beat (two to four: a reading with one is at twice the tempo); a first hit on the beat
        // (people start on it), the likeliest tempo and the unshifted beat lead a close call.
        const bpm = 60 / (beat.times ? (beat.times[beat.times.length - 1] - beat.times[0]) / (beat.times.length - 1) : beat.period);
        const slots = candidate.hits.map((hit) => hit.slot), perBeat = candidate.hits.length / Math.max(1, Math.max(...slots) - Math.min(...slots) + 1);
        candidate.value = candidate.fit + OPTIONS.soundWeight * candidate.sound - OPTIONS.errorWeight * candidate.error / onsets.length - OPTIONS.tripletWeight * candidate.triplets
          + OPTIONS.common * Math.exp(-((Math.log2(bpm / 100)) ** 2) / (2 * OPTIONS.commonSpread ** 2))
          + OPTIONS.density * Math.exp(-((Math.log2(perBeat / OPTIONS.perBeat)) ** 2) / (2 * OPTIONS.densitySpread ** 2))
          + (candidate.startsOnBeat ? 0.05 : 0) + (index ? 0 : 0.04) + (shift ? 0 : 0.02);
        if (!placed || candidate.value > placed.value) placed = candidate;
      }
    });
  }
  if (!placed) return null;
  const { hits, downbeat, beat, error } = placed;
  // Accents: well above the usual hit of the same drum.
  const usual = Object.fromEntries(["kick", "snare", "hat"].map((kind) => [kind, percentile(hits.filter((hit) => hit.kinds[0] === kind).map((hit) => hit.strength), 0.5) || 1]));
  // Up to `most` bars from the downbeat, written as a drum-set part.
  const lastSlot = Math.max(...hits.map((hit) => hit.slot)), barCount = Math.max(1, Math.min(most, Math.ceil((lastSlot - downbeat + 1e-6) / perBar)));
  const barTicks = Math.round(perBar * TICKS), measures = [];
  for (let bar = 0; bar < barCount; bar++) {
    const begins = downbeat + bar * perBar, inBar = hits.filter((hit) => hit.slot >= begins - 1e-6 && hit.slot < begins + perBar - 1e-6);
    const notes = inBar.map((hit) => { const relative = hit.strength / usual[hit.kinds[0]]; return { at: Math.min(barTicks - 1, Math.round((hit.slot - begins) * TICKS)), notes: hit.kinds.map((kind) => PITCH[kind]),
      // Loudness varies from hit to hit in any performance: only clear accents and ghost notes show.
      ornaments: relative >= 1.75 ? "a" : "", velocity: Math.max(0.25, Math.min(0.9, 0.5 * relative ** 0.35)) }; });
    measures.push({ timeSig, parts: [{ instrument: "drumset", voices: [writeVoice(notes, timeSig)] }] });
  }
  // The tempo through the bars written.
  const period = (timeAt(beat, downbeat + barCount * perBar) - timeAt(beat, downbeat)) / (barCount * perBar);
  return { score: { parts: { drumset: { enabled: true } }, measures }, tempo: given ? Math.round(tempo) : Math.round(60 / period * (timeSig.type === 8 ? 2 : 1)), timeSig, hits: hits.length,
    confidence: Math.max(0, 1 - error / hits.length / period * 6), beat: { period, phase: timeAt(beat, downbeat) - downbeat * period },
    onsets: onsets.map((onset, index) => ({ time: onset.time, kinds: hits[index].kinds })), beatboxed: onsets.voice > OPTIONS.voiceLean };
}
